import React, { useState } from "react";
import {
    DataGrid,
    GridColDef,
    GridRenderCellParams,
    GridRowSelectionModel,
    GridValueGetterParams,
} from "@mui/x-data-grid";
import { SystemMailWithoutBody } from "../../model/SystemMail";
import classes from "../../styling/components/TemplateTable.module.css";
import { useParams } from "react-router-dom";
import { Alert, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import { useConfirmationDialog } from "../../hooks/useConfirmationDialog";
import {
    getCustomerSystemMails,
    deleteSystemMail,
} from "../../api/systemMailClient";
import { Link } from "react-router-dom";
import { SendSystemMailTestmailButton } from "./SendSystemMailTestmailButton";

interface Props {
    templates: SystemMailWithoutBody[];
    selectedTemplateIds?: string[];
    onSelectionChange?: (newSelection: string[]) => void;
    readonly?: boolean;
}

const SystemMailTemplateTable: React.FC<Props> = (props) => {
    const { t } = useTranslation();
    const { customerId } = useParams();
    const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
    const [list, setList] = useState<SystemMailWithoutBody[]>(props.templates);
    const [error, setError] = useState<string | null>(null);
    const {
        selectedTemplateIds = [],
        onSelectionChange,
        readonly = true,
    } = props;

    const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
        if (!readonly && onSelectionChange) {
            onSelectionChange(newSelection as string[]);
        }
    };

    const refreshList = async () => {
        if (!customerId) return;
        try {
            const templates = await getCustomerSystemMails(customerId);
            setList(templates);
        } catch {
            setError(t("systemMailTemplate.table.loadError"));
        }
    };

    const confirmDelete = (templateId: string) => {
        const callback = async () => {
            if (!customerId) return;
            try {
                await deleteSystemMail(templateId, customerId);
                setList((prev) => prev.filter((t) => t.templateId !== templateId));
            } catch {
                setError(t("systemMailTemplate.table.deleteError"));
            }
        };
        openConfirmationDialog(
            t("systemMailTemplate.table.deleteTitle"),
            t("systemMailTemplate.table.deleteContent"),
            callback
        );
    };

    const columns: GridColDef[] = [
        {
            field: "name",
            headerName: t("systemMailTemplate.table.name"),
            flex: 1.4,
            minWidth: 150,
        },
        {
            field: "senderAddress",
            headerName: t("templateTable.senderAddress"),
            flex: 1.4,
            minWidth: 150,
        },
        {
            field: "testmail",
            headerName: "",
            sortable: false,
            width: 50,
            valueGetter: (params: GridValueGetterParams) => params.row.templateId,
            renderCell: (params: GridRenderCellParams) => (
                <SendSystemMailTestmailButton templateId={params.value as string} />
            ),
        },
        {
            field: "edit",
            headerName: "",
            sortable: false,
            width: 50,
            valueGetter: (params: GridValueGetterParams) =>
                params.row.templateId,
            renderCell: (params: GridRenderCellParams) => (
                <Link to={String(params.value)}>
                    <EditIcon />
                </Link>
            ),
        },
        {
            field: "delete",
            headerName: "",
            sortable: false,
            width: 50,
            valueGetter: (params: GridValueGetterParams) =>
                params.row.templateId,
            renderCell: (params: GridRenderCellParams) => (
                <IconButton
                    size="small"
                    aria-label="delete"
                    onClick={() => confirmDelete(params.value as string)}
                >
                    <DeleteIcon />
                </IconButton>
            ),
        },
    ];

    return (
        <>
            {confirmationDialog}
            <div className={classes.templateTable}>
                {error && (
                    <Alert
                        severity="error"
                        onClose={() => setError(null)}
                        sx={{ mb: 1 }}
                    >
                        {error}
                    </Alert>
                )}
                <DataGrid
                    rows={list}
                    getRowId={(row) => row.templateId}
                    columns={columns}
                    localeText={{
                        MuiTablePagination: {
                            labelRowsPerPage: t("dataGrid.pagination.labelRowsPerPage") || "Rows per page:",
                            labelDisplayedRows: ({ from, to, count }) =>
                                t("dataGrid.pagination.labelDisplayedRows", { from, to, count }) || `${from}-${to} of ${count}`,
                        },
                    }}
                    disableColumnMenu={true}
                    disableRowSelectionOnClick={true}
                    checkboxSelection={!readonly}
                    rowSelectionModel={readonly ? undefined : selectedTemplateIds}
                    onRowSelectionModelChange={readonly ? undefined : handleSelectionChange}
                    initialState={{
                        pagination: {
                            paginationModel: { page: 0, pageSize: 10 },
                        },
                    }}
                    pageSizeOptions={[5, 10, 25]}
                />
            </div>
        </>
    );
};

export default SystemMailTemplateTable;
